import { instance, GETREQUESTOPTIONS } from '../apiService';
import type { EditLogEntry } from '../../components/documents/EditLog';
import type {
    Category,
    CategoryUsage,
} from '../../components/ui-components/category/categoryColors';

export interface ImplantMember {
    _id: string;
    Model?: string;
    Type?: string;
    'MRI-status'?: number;
}

export interface ScannerMember {
    _id: string;
    Name?: string;
    Model?: string;
    Manufacturer?: string;
    FieldStrength?: number;
    TRCoils?: string;
}

export interface PolicyMember {
    _id: string;
    document_id?: { title?: string };
    effectiveDate?: string;
    expiryDate?: string;
    tags?: string[];
}

export interface FileMember {
    _id: string;
    filename: string;
    title?: string;
    documentType: 'document' | 'image' | 'video' | 'other';
    mimeType?: string;
    key: string;
}

export type FolderSort =
    'name-asc' | 'name-desc' | 'recent' | 'newest' | 'oldest';

export interface FolderSummary {
    _id: string;
    title: string;
    description?: string;
    isPinned: boolean;
    category?: Category;
    implantCount: number;
    scannerCount: number;
    policyCount: number;
    fileCount: number;
    createdAt: string;
    updatedAt: string;
}

export type FolderListItem = FolderSummary & {
    subFolderCount: number;
    linkCount: number;
};

export interface FolderListResult {
    pinned: FolderListItem[];
    unpinned: FolderListItem[];
    hasMore: boolean;
}

export interface FolderParentSummary {
    _id: string;
    title: string;
    category?: Category;
}

export interface FolderLink {
    _id: string;
    title: string;
    url: string;
}

export interface FolderNote {
    title: string;
    html: string;
}

export interface SubFolderSummary {
    _id: string;
    title: string;
    description?: string;
    implantCount: number;
    scannerCount: number;
    policyCount: number;
    fileCount: number;
    linkCount: number;
}

export interface FolderDetail {
    _id: string;
    title: string;
    description?: string;
    isPinned: boolean;
    category?: Category;
    parent?: FolderParentSummary;
    subFolders: SubFolderSummary[];
    links: FolderLink[];
    note?: FolderNote;
    implants: ImplantMember[];
    scanners: ScannerMember[];
    policies: PolicyMember[];
    files: FileMember[];
    editLog: EditLogEntry[];
    createdAt: string;
    updatedAt: string;
}

export interface FolderPickerItem {
    _id: string;
    title: string;
    parentId?: string;
    parentTitle?: string;
}

export type FolderForImplant = FolderPickerItem & { hasImplant: boolean };

const unwrapResponse = <T>(res: { status: number; data: T } | undefined): T => {
    if (!res || res.status >= 400) {
        throw res?.data ?? new Error('Request failed');
    }
    return res.data;
};

export const listFolders = async (input: {
    sort: FolderSort;
    limit: number;
    skip: number;
}): Promise<FolderListResult> => {
    const res = await instance.get('/api/groups', {
        ...GETREQUESTOPTIONS(),
        params: input,
    });
    return unwrapResponse(res);
};

export const getFolderById = async (id: string): Promise<FolderDetail> => {
    const res = await instance.get(`/api/groups/${id}`, GETREQUESTOPTIONS());
    return unwrapResponse(res);
};

export const getFoldersForImplant = async (
    implantId: string
): Promise<FolderForImplant[]> => {
    const res = await instance.get(
        `/api/groups/by-implant/${implantId}`,
        GETREQUESTOPTIONS()
    );
    return unwrapResponse(res);
};

export const createFolder = async (input: {
    title: string;
    description?: string;
    category?: Category | null;
    parentId?: string;
}): Promise<{ _id: string }> =>
    unwrapResponse(
        await instance.post('/api/groups', input, GETREQUESTOPTIONS())
    );

export const updateFolder = async (
    id: string,
    input: {
        title?: string;
        description?: string;
        // null clears the category; undefined leaves it unchanged
        category?: Category | null;
        renameCategory?: { from: Category; to: Category };
    }
): Promise<void> => {
    unwrapResponse(
        await instance.put(`/api/groups/${id}`, input, GETREQUESTOPTIONS())
    );
};

export const listFolderCategories = async (): Promise<CategoryUsage[]> => {
    const res = await instance.get(
        '/api/groups/categories',
        GETREQUESTOPTIONS()
    );
    return unwrapResponse(res);
};

export const deleteFolder = async (id: string): Promise<void> => {
    unwrapResponse(
        await instance.delete(`/api/groups/${id}`, GETREQUESTOPTIONS())
    );
};

export const setFolderPinned = async (
    id: string,
    isPinned: boolean
): Promise<void> => {
    unwrapResponse(
        await instance.put(
            `/api/groups/${id}/pin`,
            { isPinned },
            GETREQUESTOPTIONS()
        )
    );
};

export const addImplantsToFolder = async (
    id: string,
    implantIds: string[]
): Promise<{ added: number }> => {
    const res = await instance.post(
        `/api/groups/${id}/implants`,
        { implantIds },
        GETREQUESTOPTIONS()
    );
    return unwrapResponse(res);
};

export const removeImplantsFromFolder = async (
    id: string,
    implantIds: string[]
): Promise<{ removed: number }> => {
    const res = await instance.delete(`/api/groups/${id}/implants`, {
        ...GETREQUESTOPTIONS(),
        data: { implantIds },
    });
    return unwrapResponse(res);
};

export const addScannersToFolder = async (
    id: string,
    scannerIds: string[]
): Promise<{ added: number }> => {
    const res = await instance.post(
        `/api/groups/${id}/scanners`,
        { scannerIds },
        GETREQUESTOPTIONS()
    );
    return unwrapResponse(res);
};

export const removeScannersFromFolder = async (
    id: string,
    scannerIds: string[]
): Promise<{ removed: number }> => {
    const res = await instance.delete(`/api/groups/${id}/scanners`, {
        ...GETREQUESTOPTIONS(),
        data: { scannerIds },
    });
    return unwrapResponse(res);
};

export const addPoliciesToFolder = async (
    id: string,
    policyIds: string[]
): Promise<{ added: number }> => {
    const res = await instance.post(
        `/api/groups/${id}/policies`,
        { policyIds },
        GETREQUESTOPTIONS()
    );
    return unwrapResponse(res);
};

export const removePoliciesFromFolder = async (
    id: string,
    policyIds: string[]
): Promise<{ removed: number }> => {
    const res = await instance.delete(`/api/groups/${id}/policies`, {
        ...GETREQUESTOPTIONS(),
        data: { policyIds },
    });
    return unwrapResponse(res);
};

export const addFilesToFolder = async (
    id: string,
    fileIds: string[]
): Promise<{ added: number }> => {
    const res = await instance.post(
        `/api/groups/${id}/files`,
        { fileIds },
        GETREQUESTOPTIONS()
    );
    return unwrapResponse(res);
};

export const removeFilesFromFolder = async (
    id: string,
    fileIds: string[]
): Promise<{ removed: number }> => {
    const res = await instance.delete(`/api/groups/${id}/files`, {
        ...GETREQUESTOPTIONS(),
        data: { fileIds },
    });
    return unwrapResponse(res);
};

export const setFoldersForImplant = async (
    implantId: string,
    folderIds: string[]
): Promise<void> => {
    unwrapResponse(
        await instance.put(
            `/api/groups/by-implant/${implantId}`,
            { folderIds },
            GETREQUESTOPTIONS()
        )
    );
};

export const listFolderOptions = async (): Promise<FolderPickerItem[]> =>
    unwrapResponse(
        await instance.get('/api/groups/options', GETREQUESTOPTIONS())
    );

export const setFoldersForPolicy = async (
    policyId: string,
    folderIds: string[]
): Promise<void> => {
    unwrapResponse(
        await instance.put(
            `/api/groups/by-policy/${policyId}`,
            { folderIds },
            GETREQUESTOPTIONS()
        )
    );
};

export const addFolderLink = async (
    folderId: string,
    input: { title: string; url: string }
): Promise<{ _id: string }> =>
    unwrapResponse(
        await instance.post(
            `/api/groups/${folderId}/links`,
            input,
            GETREQUESTOPTIONS()
        )
    );

export const updateFolderLink = async (
    folderId: string,
    linkId: string,
    input: { title: string; url: string }
): Promise<void> => {
    unwrapResponse(
        await instance.put(
            `/api/groups/${folderId}/links/${linkId}`,
            input,
            GETREQUESTOPTIONS()
        )
    );
};

export const removeFolderLink = async (
    folderId: string,
    linkId: string
): Promise<void> => {
    unwrapResponse(
        await instance.delete(
            `/api/groups/${folderId}/links/${linkId}`,
            GETREQUESTOPTIONS()
        )
    );
};

export const updateFolderNote = async (
    folderId: string,
    note: FolderNote | null
): Promise<void> => {
    unwrapResponse(
        await instance.put(
            `/api/groups/${folderId}`,
            { note },
            GETREQUESTOPTIONS()
        )
    );
};
